/**
 * DoclingConfigurationPanel
 *
 * Allows users to configure a Docling SaaS API key so that their documents
 * are converted by the cloud service instead of the local in-container
 * pipeline.  When no key is configured, conversion falls back to local.
 */

import { useState } from 'react';
import {
  Button,
  TextInput,
  Tile,
  Toggle,
  InlineNotification,
  SkeletonText,
  Tag,
  Loading,
} from '@carbon/react';
import { Settings, TrashCan, Checkmark, WarningAlt, View, ViewOff } from '@carbon/icons-react';
import {
  useDoclingConfig,
  useDoclingOverrideStatus,
  useSaveDoclingConfig,
  useDeleteDoclingConfig,
  useTestDoclingKey,
} from '../../lib/query/hooks/useDoclingConfig';
import './DoclingConfigurationPanel.scss';

export default function DoclingConfigurationPanel() {
  const [apiKey, setApiKey] = useState('');
  const [saasUrl, setSaasUrl] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: overrideStatus } = useDoclingOverrideStatus();
  const { data: config, isLoading, error } = useDoclingConfig();
  const saveMutation = useSaveDoclingConfig();
  const deleteMutation = useDeleteDoclingConfig();
  const testMutation = useTestDoclingKey();

  // `configured` is the canonical flag in the shared DoclingConfig type
  const isConfigured = !!config?.configured;

  const handleTest = async () => {
    if (!apiKey.trim()) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({
        apiKey: apiKey.trim(),
        ...(saasUrl.trim() && { saasUrl: saasUrl.trim() }),
      });
      setTestResult(
        result.isValid
          ? { ok: true, message: 'API key is valid — Docling SaaS is reachable.' }
          : { ok: false, message: result.error ?? 'API key test failed.' }
      );
    } catch {
      setTestResult({ ok: false, message: 'Could not reach the Docling SaaS service.' });
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) return;
    setSaveError(null);
    try {
      await saveMutation.mutateAsync({
        apiKey: apiKey.trim(),
        ...(saasUrl.trim() && { saasUrl: saasUrl.trim() }),
      });
      setApiKey('');
      setSaasUrl('');
      setShowKey(false);
      setTestResult(null);
    } catch {
      setSaveError('Failed to save Docling configuration. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove Docling SaaS key? Documents will be converted locally.')) return;
    setSaveError(null);
    try {
      await deleteMutation.mutateAsync();
      setTestResult(null);
    } catch {
      setSaveError('Failed to remove Docling configuration.');
    }
  };

  // ── Platform-global override banner ───────────────────────────────────────
  // When a deployment-wide Docling SaaS key is configured by the platform
  // admin, individual users cannot (and do not need to) configure their own.
  if (overrideStatus?.overrideActive) {
    const label = overrideStatus.displayName ?? 'IBM Docling SaaS';
    const url = overrideStatus.saasUrl;
    return (
      <Tile className="docling-config-panel">
        <div className="docling-config-panel__header">
          <div className="docling-config-panel__header-left">
            <Settings size={24} />
            <h3>Document Conversion</h3>
          </div>
        </div>
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Platform Global Setting —"
          subtitle={`Document conversion is managed by your platform administrator using ${label}.${url ? ` Endpoint: ${url}` : ''} No individual API key is required.`}
          style={{ marginBottom: '1rem' }}
        />
        <div className="docling-config-panel__status">
          <div className="docling-config-panel__status-row">
            <Checkmark size={16} className="docling-config-panel__status-icon--ok" />
            <span>Docling SaaS is active (platform-configured).</span>
            <Tag type="green" size="sm">Active</Tag>
          </div>
        </div>
      </Tile>
    );
  }

  if (isLoading) {
    return (
      <Tile className="docling-config-panel">
        <div className="docling-config-panel__header">
          <Settings size={24} />
          <h3>Document Conversion</h3>
        </div>
        <SkeletonText paragraph lineCount={3} />
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile className="docling-config-panel">
        <InlineNotification
          kind="error"
          title="Failed to load Docling configuration"
          subtitle="Please try again later"
          lowContrast
        />
      </Tile>
    );
  }

  return (
    <Tile className="docling-config-panel">
      {/* Header */}
      <div className="docling-config-panel__header">
        <div className="docling-config-panel__header-left">
          <Settings size={24} />
          <h3>Document Conversion</h3>
        </div>
        {isConfigured && (
          <Button
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            iconDescription="Remove key"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            Remove key
          </Button>
        )}
      </div>

      {/* Current status */}
      <div className="docling-config-panel__status">
        {isConfigured ? (
          <div className="docling-config-panel__status-row">
            <Checkmark size={16} className="docling-config-panel__status-icon--ok" />
            <span>
              Docling SaaS is configured.
              {config?.saasUrl && (
                <span className="docling-config-panel__url"> Endpoint: {config.saasUrl}</span>
              )}
            </span>
            <Tag type="green" size="sm">Active</Tag>
          </div>
        ) : (
          <div className="docling-config-panel__status-row">
            <WarningAlt size={16} className="docling-config-panel__status-icon--warn" />
            <span>No SaaS key configured — using local (in-container) conversion.</span>
            <Tag type="gray" size="sm">Local</Tag>
          </div>
        )}
      </div>

      <p className="docling-config-panel__description">
        If you have a{' '}
        <a href="https://docling.ai" target="_blank" rel="noopener noreferrer">
          Docling SaaS
        </a>{' '}
        subscription, enter your API key below to use the cloud conversion service.
        Without a key, documents are converted by the local Docling pipeline running in the container.
      </p>

      {/* Form */}
      <div className="docling-config-panel__form">
        <div className="docling-config-panel__key-row">
          <TextInput
            id="docling-saas-api-key"
            labelText={isConfigured ? 'New API Key (leave blank to keep current)' : 'Docling SaaS API Key'}
            type={showKey ? 'text' : 'password'}
            placeholder="Enter your Docling SaaS API key"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setTestResult(null);
            }}
            helperText="Your key will be encrypted and stored securely."
          />
          <Button
            kind="ghost"
            size="sm"
            renderIcon={showKey ? ViewOff : View}
            iconDescription={showKey ? 'Hide key' : 'Show key'}
            hasIconOnly
            onClick={() => setShowKey(!showKey)}
            className="docling-config-panel__eye-btn"
          />
        </div>

        <TextInput
          id="docling-saas-url"
          labelText="SaaS Endpoint URL (optional)"
          type="text"
          placeholder="https://docling.example.com (leave blank for default)"
          value={saasUrl}
          onChange={(e) => setSaasUrl(e.target.value)}
          helperText="Override the default Docling SaaS endpoint, e.g. for an on-prem deployment."
        />

        <Toggle
          id="docling-show-key"
          labelText="Show API key"
          size="sm"
          toggled={showKey}
          onToggle={(checked) => setShowKey(checked)}
        />

        <div className="docling-config-panel__actions">
          <Button
            kind="secondary"
            size="md"
            onClick={handleTest}
            disabled={!apiKey.trim() || testMutation.isPending}
          >
            {testMutation.isPending ? (
              <><Loading small withOverlay={false} /> Testing…</>
            ) : (
              'Test API Key'
            )}
          </Button>

          <Button
            kind="primary"
            size="md"
            onClick={handleSave}
            disabled={!apiKey.trim() || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <><Loading small withOverlay={false} /> Saving…</>
            ) : (
              isConfigured ? 'Update Key' : 'Save Key'
            )}
          </Button>
        </div>

        {testResult && (
          <InlineNotification
            kind={testResult.ok ? 'success' : 'error'}
            title={testResult.ok ? 'API Key Valid' : 'Test Failed'}
            subtitle={testResult.message}
            lowContrast
            style={{ marginTop: '1rem' }}
          />
        )}

        {saveError && (
          <InlineNotification
            kind="error"
            title="Save error"
            subtitle={saveError}
            lowContrast
            style={{ marginTop: '1rem' }}
          />
        )}

        {saveMutation.isSuccess && (
          <InlineNotification
            kind="success"
            title="Saved"
            subtitle="Docling SaaS key saved successfully."
            lowContrast
            style={{ marginTop: '1rem' }}
          />
        )}
      </div>
    </Tile>
  );
}

// Made with Bob
